import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Chip } from '@openx/components-table/ChipFilters/Chip';
import { useTranslation } from 'react-i18next';
import type { AppliedSegmentsFilterDisplay } from '../utils/getAppliedSegmentsFiltersDisplay';

type ExportSegmentsDialogContentProps = {
  appliedFilters: AppliedSegmentsFilterDisplay[];
  segmentsCount: number | null;
  countLoading?: boolean;
};

const formatFilterLabel = (filter: AppliedSegmentsFilterDisplay) => `${filter.label}: ${filter.value}`;

export const ExportSegmentsDialogContent = ({
  appliedFilters,
  segmentsCount,
  countLoading = false,
}: ExportSegmentsDialogContentProps) => {
  const { t } = useTranslation();
  const isCountReady = !countLoading && segmentsCount !== null;

  return (
    <DialogContent data-test="export-segments-dialog-content" dividers sx={{ pb: 1 }}>
      <Typography color="textPrimary" sx={{ mb: 1 }}>
        {t('You’re about to export and download')}{' '}
        {isCountReady ? (
          <Box component="span" sx={{ fontWeight: 'fontWeightBold' }} data-test="export-segments-count-value">
            {segmentsCount}
          </Box>
        ) : (
          <CircularProgress
            size={14}
            sx={{ mx: 0.5, verticalAlign: 'middle' }}
            data-test="export-segments-count-loading"
          />
        )}{' '}
        {t('segments matching the current list filters.')}
      </Typography>

      <Box data-test="export-segments-filters" sx={{ mb: 1 }}>
        <Typography
          color="text.primary"
          variant="overline"
          sx={{
            display: 'block',
            fontWeight: 'fontWeightBold',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          {t('Applied filters')}:
        </Typography>

        {appliedFilters.length === 0 ? (
          <Typography color="text.secondary" data-test="export-segments-no-filters">
            {t('No filters applied.')}
          </Typography>
        ) : (
          <Stack direction="row" useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {appliedFilters.map(filter => (
              <Chip
                key={filter.label}
                data-test="export-segments-filter-chip"
                label={formatFilterLabel(filter)}
                sx={{ paddingRight: 1.5 }}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Typography color="textPrimary">{t('Do you want to proceed?')}</Typography>
    </DialogContent>
  );
};
